import { ContactOverviewModel } from "src/types/contact/ContactOverviewModel"
import { ContactContainerType } from "src/types/contact/ContactContainer"
import { ContactFilterValue } from "src/types/contact/ContactFilter"
import { storage, storageHelpers, CACHE_KEY_PREFIX, TTL_CONFIG } from "src/storage/mmkv"
import { CacheMonitor } from "./CacheMonitor"

/**
 * キャッシュデータ構造
 */
export interface ContactListCache {
  version: string // スキーマバージョン（マイグレーション用）
  teamMemberId: string
  containerType: ContactContainerType
  filters: ContactFilterValue[]
  searchText?: string

  // データ
  contacts: ContactOverviewModel[]
  nextToken?: string
  total?: number

  // メタデータ
  cachedAt: number // キャッシュ保存時刻 (Unix timestamp)
  expiresAt: number // 有効期限 (Unix timestamp)
  pages: number // キャッシュされたページ数
}

/**
 * ページネーション対応キャッシュデータ構造
 * 最大5000件（100ページ）まで対応
 */
export interface ContactListPaginationCache {
  version: string
  teamMemberId: string
  containerType: ContactContainerType
  filters: ContactFilterValue[]
  searchText?: string // 検索テキストもキーに含める（データ整合性のため）

  // ページごとのデータ
  pages: {
    [pageIndex: number]: {
      contacts: ContactOverviewModel[]
      nextToken?: string
      cachedAt: number
      pageSize: number
    }
  }

  // 全体のメタデータ
  total?: number
  firstCachedAt: number // 最初にキャッシュされた時刻
  lastUpdatedAt: number // 最後に更新された時刻
  expiresAt: number
  maxPageIndex: number // 現在キャッシュされている最大ページインデックス
  totalCachedContacts: number // キャッシュされた連絡の総数
}

/**
 * 連絡詳細キャッシュデータ構造
 */
export interface ContactDetailCache {
  version: string
  contactId: string
  contactType: "Inbox" | "Outbox" | "Reserved" | "Migration"
  data: any // Item型（実際のデータ構造）
  cachedAt: number
  expiresAt: number
}

/**
 * 返信スレッドキャッシュデータ構造
 */
export interface ReplyThreadsCache {
  version: string
  contactId: string
  viewMode: "Inbox" | "Outbox"
  data: any[] // ContactReplyThreadOverview[]
  cachedAt: number
  expiresAt: number
}

/**
 * チームメンバー回答状況キャッシュデータ構造
 */
export interface TeamMemberContactsCache {
  version: string
  contactId: string
  type: "Inbox" | "Outbox" | "Reserved"
  data: any // TeamMemberContacts
  cachedAt: number
  expiresAt: number
}

/**
 * キャッシュ無効化イベント
 */
export enum CacheInvalidationEvent {
  PULL_TO_REFRESH = "pull_to_refresh", // 最優先
  PUSH_NOTIFICATION_OPENED = "push_opened", // 新着連絡通知
  CONTACT_SENT = "contact_sent",
  CONTACT_UPDATED = "contact_updated",
  CONTACT_DELETED = "contact_deleted",
  FAVORITE_TOGGLED = "favorite_toggled",
  READ_STATUS_CHANGED = "read_status_changed",
}

/**
 * 連絡一覧キャッシュサービス
 *
 * MMKVを使用した高速なローカルキャッシュ管理
 */
export class ContactListCacheService {
  private static readonly CURRENT_VERSION = "3.0" // ページネーションキャッシュ対応のためバージョンアップ
  private static readonly MAX_CACHED_PAGES = 100 // 最大100ページ（5000件）まで
  private static readonly DEFAULT_PAGE_SIZE = 50 // デフォルトページサイズ

  // 自己修復機能のためのプロパティ
  private static corruptionAttempts = new Map<string, number>()
  private static readonly MAX_REPAIR_ATTEMPTS = 3
  private static readonly monitor = CacheMonitor.getInstance()

  /**
   * キャッシュキーを生成
   */
  private static generateCacheKey(params: {
    teamMemberId: string
    containerType: ContactContainerType
    filters: ContactFilterValue[]
    searchText?: string
    pageIndex?: number
  }): string {
    const filterHash = this.generateFilterHash(params.filters, params.searchText)
    const pageStr = params.pageIndex !== undefined ? `_page${params.pageIndex}` : ""

    return `${CACHE_KEY_PREFIX.CONTACT_LIST}_${params.teamMemberId}_${params.containerType}_${filterHash}${pageStr}`
  }

  /**
   * ページネーションキャッシュキーを生成（全ページ統合用）
   */
  private static generatePaginationCacheKey(params: {
    teamMemberId: string
    containerType: ContactContainerType
    filters: ContactFilterValue[]
    searchText?: string
  }): string {
    const filterHash = this.generateFilterHash(params.filters, params.searchText)
    return `${CACHE_KEY_PREFIX.CONTACT_LIST}_${params.teamMemberId}_${params.containerType}_${filterHash}_pagination`
  }

  /**
   * フィルターハッシュを生成
   * 注: 検索テキストも含めてキーを生成（データ整合性のため）
   */
  private static generateFilterHash(filters: ContactFilterValue[], searchText?: string): string {
    // 検索テキストも含めてハッシュを生成（空文字列として正規化）
    const filterStr = JSON.stringify({
      filters: filters.sort(),
      search: (searchText || "").trim().toLowerCase(), // 正規化
    })
    // 簡易ハッシュ（本番では crypto.subtle を使用可能）
    return this.hashCode(filterStr).toString(16).slice(0, 8) // 8文字に拡張
  }

  /**
   * 簡易ハッシュ関数
   */
  private static hashCode(str: string): number {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = (hash << 5) - hash + char
      hash = hash & hash // Convert to 32-bit integer
    }
    return Math.abs(hash)
  }

  /**
   * チェックサムを計算
   * Note: publicメソッドとして公開し、テスト可能にしている
   */
  static calculateChecksum(data: any): string {
    try {
      const json = JSON.stringify(data)
      let hash = 0
      for (let i = 0; i < json.length; i++) {
        const char = json.charCodeAt(i)
        hash = (hash << 5) - hash + char
        hash = hash & hash // Convert to 32-bit integer
      }
      return hash.toString(36)
    } catch (error) {
      console.error("[Cache] Error calculating checksum", error)
      return ""
    }
  }

  /**
   * チェックサム付きデータを作成
   */
  private static addChecksum(data: any): any {
    const checksum = ContactListCacheService.calculateChecksum(data)
    return {
      ...data,
      __checksum: checksum,
      __timestamp: Date.now(),
    }
  }

  /**
   * チェックサムを検証
   * Note: publicメソッドとして公開し、テスト可能にしている
   */
  static verifyChecksum(data: any): boolean {
    if (!data || !data.__checksum) {
      return false
    }

    const { __checksum, __timestamp, ...originalData } = data
    const calculatedChecksum = ContactListCacheService.calculateChecksum(originalData)
    const isValid = __checksum === calculatedChecksum

    if (!isValid) {
      console.warn("[Cache] Checksum verification failed", {
        expected: __checksum,
        calculated: calculatedChecksum,
      })
    }

    return isValid
  }

  /**
   * バックアップを作成
   */
  static createBackup(key: string): boolean {
    try {
      const data = storageHelpers.getObject(key)
      if (data && ContactListCacheService.verifyChecksum(data)) {
        const backupKey = `${key}_backup`
        storageHelpers.setObject(backupKey, data)
        console.log("[Cache] Backup created", { key, backupKey })
        return true
      }
      return false
    } catch (error) {
      console.error("[Cache] Error creating backup", error)
      this.monitor.recordError(error as Error, "createBackup")
      return false
    }
  }

  /**
   * バックアップから復元
   */
  private static getBackup(key: string): any {
    try {
      const backupKey = `${key}_backup`
      const backup = storageHelpers.getObject(backupKey)
      if (backup && ContactListCacheService.verifyChecksum(backup)) {
        console.log("[Cache] Restored from backup", { key })
        return backup
      }
      return null
    } catch (error) {
      console.error("[Cache] Error getting backup", error)
      return null
    }
  }

  /**
   * 破損データの修復を試行
   */
  private static attemptRepair(key: string): any {
    const attempts = (this.corruptionAttempts.get(key) || 0) + 1
    this.corruptionAttempts.set(key, attempts)

    console.warn(`[Cache] Corruption detected, attempt ${attempts}/${this.MAX_REPAIR_ATTEMPTS}`, { key })

    if (attempts >= this.MAX_REPAIR_ATTEMPTS) {
      // 修復失敗 - キャッシュを削除
      console.error("[Cache] Max repair attempts reached, deleting corrupted data", { key })
      storageHelpers.delete(key)
      storageHelpers.delete(`${key}_backup`)
      this.corruptionAttempts.delete(key)
      return null
    }

    // 戦略1: バックアップから復元
    const backup = this.getBackup(key)
    if (backup) {
      storageHelpers.setObject(key, backup)
      this.monitor.recordHit() // バックアップ復元成功
      return backup
    }

    // 戦略2: 部分的なデータ復元を試みる（現時点では単純に削除）
    // TODO: 実際のストレージ実装に応じて部分復元ロジックを実装
    console.log("[Cache] Partial recovery not available, clearing cache")

    // 戦略3: キャッシュをクリアして再取得を促す
    storageHelpers.delete(key)
    this.monitor.recordMiss()
    return null
  }

  /**
   * 有効なJSON部分を抽出
   */
  private static extractValidJson(raw: string): string | null {
    try {
      // 最初の { から最後の } までを抽出
      const start = raw.indexOf("{")
      const end = raw.lastIndexOf("}")

      if (start !== -1 && end !== -1 && end > start) {
        return raw.substring(start, end + 1)
      }
    } catch (error) {
      console.error("[Cache] Error extracting JSON", error)
    }
    return null
  }

  /**
   * ページネーションキャッシュを取得（全ページ統合）
   */
  static getPaginationCache(
    params: {
      teamMemberId: string
      containerType: ContactContainerType
      filters: ContactFilterValue[]
      searchText?: string
    },
    options?: {
      ignoreExpiration?: boolean // TTL期限切れでもキャッシュを返す（オフライン時のフォールバック用）
    }
  ): ContactListPaginationCache | null {
    try {
      const cacheKey = this.generatePaginationCacheKey(params)

      let cached: ContactListPaginationCache | null = null
      try {
        cached = storageHelpers.getObject<ContactListPaginationCache>(cacheKey)
      } catch (storageError) {
        console.warn("[Pagination Cache] Storage access error:", storageError)
        return null
      }

      if (!cached) {
        console.log("[Pagination Cache] MISS - No cache found")
        return null
      }

      // バージョンチェック
      if (cached.version !== this.CURRENT_VERSION) {
        console.warn("[Pagination Cache] Version mismatch - Invalidating")
        this.deletePaginationCache(params)
        return null
      }

      const now = Date.now()

      // TTL確認（ignoreExpirationオプションが有効な場合はスキップ）
      if (!options?.ignoreExpiration && cached.expiresAt <= now) {
        console.log("[Pagination Cache] EXPIRED - TTL exceeded")
        this.deletePaginationCache(params)
        return null
      }

      if (options?.ignoreExpiration && cached.expiresAt <= now) {
        console.log("[Pagination Cache] EXPIRED but using as fallback (ignoreExpiration=true)", {
          totalPages: Object.keys(cached.pages).length,
          totalContacts: cached.totalCachedContacts,
          maxPageIndex: cached.maxPageIndex,
        })
      } else {
        console.log("[Pagination Cache] HIT", {
          totalPages: Object.keys(cached.pages).length,
          totalContacts: cached.totalCachedContacts,
          maxPageIndex: cached.maxPageIndex,
        })
      }

      return cached
    } catch (error) {
      console.error("[Pagination Cache] Error getting cache", error)
      return null
    }
  }

  /**
   * ページネーションキャッシュの特定ページを更新
   */
  static updatePaginationCachePage(
    params: {
      teamMemberId: string
      containerType: ContactContainerType
      filters: ContactFilterValue[]
      searchText?: string
      pageIndex: number
    },
    data: {
      contacts: ContactOverviewModel[]
      nextToken?: string
      total?: number
    }
  ): void {
    try {
      const cacheKey = this.generatePaginationCacheKey(params)
      const now = Date.now()
      const ttl = (TTL_CONFIG as Record<string, number>)[params.containerType] || TTL_CONFIG.inbox

      // 既存のキャッシュを取得または新規作成
      let paginationCache = this.getPaginationCache(params)

      if (!paginationCache) {
        // 新規作成
        paginationCache = {
          version: this.CURRENT_VERSION,
          teamMemberId: params.teamMemberId,
          containerType: params.containerType,
          filters: params.filters,
          searchText: params.searchText,
          pages: {},
          total: data.total,
          firstCachedAt: now,
          lastUpdatedAt: now,
          expiresAt: now + ttl,
          maxPageIndex: 0,
          totalCachedContacts: 0,
        }
      }

      // ページ数制限チェック
      if (params.pageIndex >= this.MAX_CACHED_PAGES) {
        console.warn("[Pagination Cache] Max pages limit reached", {
          pageIndex: params.pageIndex,
          limit: this.MAX_CACHED_PAGES,
        })
        return
      }

      // ページデータを更新
      const previousPageSize = paginationCache.pages[params.pageIndex]?.contacts.length || 0

      paginationCache.pages[params.pageIndex] = {
        contacts: data.contacts,
        nextToken: data.nextToken,
        cachedAt: now,
        pageSize: data.contacts.length,
      }

      // メタデータを更新
      paginationCache.lastUpdatedAt = now
      paginationCache.maxPageIndex = Math.max(paginationCache.maxPageIndex, params.pageIndex)
      paginationCache.totalCachedContacts = paginationCache.totalCachedContacts - previousPageSize + data.contacts.length
      if (data.total !== undefined) {
        paginationCache.total = data.total
      }

      // 保存
      storageHelpers.setObject(cacheKey, paginationCache)

      console.log("[Pagination Cache] Page updated", {
        pageIndex: params.pageIndex,
        pageSize: data.contacts.length,
        totalPages: Object.keys(paginationCache.pages).length,
        totalCachedContacts: paginationCache.totalCachedContacts,
      })
    } catch (error) {
      console.error("[Pagination Cache] Error updating page", error)
    }
  }

  /**
   * ページネーションキャッシュから特定範囲の連絡を取得
   */
  static getContactsFromPaginationCache(params: {
    teamMemberId: string
    containerType: ContactContainerType
    filters: ContactFilterValue[]
    searchText?: string
    startIndex: number
    endIndex: number
  }): ContactOverviewModel[] | null {
    try {
      const paginationCache = this.getPaginationCache(params)
      if (!paginationCache) {
        return null
      }

      const contacts: ContactOverviewModel[] = []
      const startPage = Math.floor(params.startIndex / this.DEFAULT_PAGE_SIZE)
      const endPage = Math.floor(params.endIndex / this.DEFAULT_PAGE_SIZE)

      for (let pageIndex = startPage; pageIndex <= endPage; pageIndex++) {
        const page = paginationCache.pages[pageIndex]
        if (!page) {
          // ページがキャッシュにない場合はnullを返す（部分的なキャッシュ）
          console.log("[Pagination Cache] Missing page", { pageIndex })
          return null
        }

        const pageStartIndex = pageIndex * this.DEFAULT_PAGE_SIZE
        const pageEndIndex = pageStartIndex + page.contacts.length

        // ページ内の必要な範囲を抽出
        const relativeStart = Math.max(0, params.startIndex - pageStartIndex)
        const relativeEnd = Math.min(page.contacts.length, params.endIndex - pageStartIndex)

        if (relativeEnd > relativeStart) {
          contacts.push(...page.contacts.slice(relativeStart, relativeEnd))
        }
      }

      console.log("[Pagination Cache] Retrieved contacts", {
        requestedRange: `${params.startIndex}-${params.endIndex}`,
        returnedCount: contacts.length,
      })

      return contacts
    } catch (error) {
      console.error("[Pagination Cache] Error getting contacts", error)
      return null
    }
  }

  /**
   * ページネーションキャッシュを削除
   */
  static deletePaginationCache(params: {
    teamMemberId: string
    containerType: ContactContainerType
    filters: ContactFilterValue[]
    searchText?: string
  }): void {
    try {
      const cacheKey = this.generatePaginationCacheKey(params)
      storageHelpers.delete(cacheKey)
      console.log("[Pagination Cache] Deleted", { key: cacheKey })
    } catch (error) {
      console.error("[Pagination Cache] Error deleting cache", error)
    }
  }

  /**
   * キャッシュを取得（後方互換性のため維持）
   */
  static get(
    params: {
      teamMemberId: string
      containerType: ContactContainerType
      filters: ContactFilterValue[]
      searchText?: string
      pageIndex?: number
    },
    options?: {
      ignoreExpiration?: boolean // TTL期限切れでもキャッシュを返す（オフライン時のフォールバック用）
    }
  ): ContactListCache | null {
    try {
      const startTime = Date.now()
      const cacheKey = this.generateCacheKey(params)

      // Web環境: localStorage読み込みをtry-catchで囲む（Reactサスペンス回避）
      let cached: ContactListCache | null = null
      try {
        cached = storageHelpers.getObject<ContactListCache>(cacheKey)
      } catch (storageError) {
        console.warn("[Cache] Storage access error (Web environment):", storageError)
        this.monitor.recordError(storageError as Error, "get")
        return null
      }

      if (!cached) {
        console.log("[Cache] MISS - No cache found")
        this.monitor.recordMiss(Date.now() - startTime)
        return null
      }

      // チェックサム検証（自己修復機能）
      const cachedWithChecksum = cached as any
      if (cachedWithChecksum.__checksum) {
        if (!ContactListCacheService.verifyChecksum(cachedWithChecksum)) {
          console.warn("[Cache] Checksum verification failed - attempting repair")
          // 修復を試行
          const repaired = ContactListCacheService.attemptRepair(cacheKey)
          if (repaired) {
            cached = repaired as ContactListCache
          } else {
            this.monitor.recordError(new Error("Cache corruption"), "get")
            return null
          }
        }
      }

      // バージョンチェック
      if (cached.version !== this.CURRENT_VERSION) {
        console.warn("[Cache] Version mismatch - Invalidating cache")
        this.delete(params)
        return null
      }

      const now = Date.now()

      // TTL確認（ignoreExpirationオプションが有効な場合はスキップ）
      if (!options?.ignoreExpiration && cached.expiresAt <= now) {
        console.log("[Cache] EXPIRED - TTL exceeded")
        this.delete(params)
        this.monitor.recordEviction()
        return null
      }

      if (options?.ignoreExpiration && cached.expiresAt <= now) {
        console.log("[Cache] EXPIRED but using as fallback (ignoreExpiration=true)", {
          cachedAt: new Date(cached.cachedAt).toISOString(),
          expiresAt: new Date(cached.expiresAt).toISOString(),
          itemCount: cached.contacts.length,
        })
      } else {
        console.log("[Cache] HIT - Valid cache found", {
          cachedAt: new Date(cached.cachedAt).toISOString(),
          expiresAt: new Date(cached.expiresAt).toISOString(),
          itemCount: cached.contacts.length,
        })
      }

      this.monitor.recordHit(Date.now() - startTime)
      return cached
    } catch (error) {
      console.error("[Cache] Error getting cache", error)
      this.monitor.recordError(error as Error, "get")
      return null
    }
  }

  /**
   * キャッシュを保存
   */
  static set(
    params: {
      teamMemberId: string
      containerType: ContactContainerType
      filters: ContactFilterValue[]
      searchText?: string
      pageIndex?: number
    },
    data: {
      contacts: ContactOverviewModel[]
      nextToken?: string
      total?: number
    }
  ): void {
    try {
      const cacheKey = this.generateCacheKey(params)
      const ttl = (TTL_CONFIG as Record<string, number>)[params.containerType] || TTL_CONFIG.inbox
      const now = Date.now()

      const cache: ContactListCache = {
        version: this.CURRENT_VERSION,
        teamMemberId: params.teamMemberId,
        containerType: params.containerType,
        filters: params.filters,
        searchText: params.searchText,
        contacts: data.contacts,
        nextToken: data.nextToken,
        total: data.total,
        cachedAt: now,
        expiresAt: now + ttl,
        pages: (params.pageIndex ?? 0) + 1,
      }

      // チェックサムを追加
      const cacheWithChecksum = this.addChecksum(cache)

      // 保存
      storageHelpers.setObject(cacheKey, cacheWithChecksum)

      // 保存後の検証
      const saved = storageHelpers.getObject(cacheKey)
      if (saved && !ContactListCacheService.verifyChecksum(saved)) {
        console.error("[Cache] Save verification failed")
        this.monitor.recordError(new Error("Save verification failed"), "set")
        return
      }

      // バックアップを作成（重要なデータの場合）
      if (data.contacts.length > 0) {
        this.createBackup(cacheKey)
      }

      console.log("[Cache] SET - Cache saved with checksum", {
        key: cacheKey,
        ttl: `${ttl / 1000 / 60 / 60}h`,
        itemCount: data.contacts.length,
        hasChecksum: true,
      })
    } catch (error) {
      console.error("[Cache] Error setting cache", error)
      this.monitor.recordError(error as Error, "set")
    }
  }

  /**
   * 特定のキャッシュを削除
   */
  static delete(params: {
    teamMemberId: string
    containerType: ContactContainerType
    filters: ContactFilterValue[]
    searchText?: string
    pageIndex?: number
  }): void {
    try {
      const cacheKey = this.generateCacheKey(params)
      storageHelpers.delete(cacheKey)
      console.log("[Cache] DELETE - Cache deleted", { key: cacheKey })
    } catch (error) {
      console.error("[Cache] Error deleting cache", error)
    }
  }

  /**
   * 特定のコンテナのキャッシュを全て削除（ページネーションキャッシュも含む）
   */
  static invalidateContainer(teamMemberId: string, containerType: ContactContainerType): void {
    try {
      const pattern = `${CACHE_KEY_PREFIX.CONTACT_LIST}_${teamMemberId}_${containerType}_*`
      storageHelpers.deleteKeysWithPrefix(pattern)
      console.log("[Cache] INVALIDATE_CONTAINER", { teamMemberId, containerType })
    } catch (error) {
      console.error("[Cache] Error invalidating container", error)
    }
  }

  /**
   * 特定のチームメンバーの全キャッシュを削除
   */
  static invalidateAll(teamMemberId: string): void {
    try {
      const pattern = `${CACHE_KEY_PREFIX.CONTACT_LIST}_${teamMemberId}_*`
      storageHelpers.deleteKeysWithPrefix(pattern)
      console.log("[Cache] INVALIDATE_ALL", { teamMemberId })
    } catch (error) {
      console.error("[Cache] Error invalidating all caches", error)
    }
  }

  /**
   * 特定の連絡のお気に入り状態を更新
   */
  static updateContactFavorite(params: { teamMemberId: string; contactId: string; isFavorite: boolean }): void {
    try {
      const { teamMemberId, contactId, isFavorite } = params
      console.log("[Cache] updateContactFavorite:", { teamMemberId, contactId, isFavorite })

      // すべてのコンテナのキャッシュキーを取得（お気に入りは全コンテナに影響）
      const allKeys = storageHelpers.getAllKeys()
      const matchingKeys = allKeys.filter((key: string) => key.includes(`${CACHE_KEY_PREFIX.CONTACT_LIST}_${teamMemberId}_`))

      // 各キャッシュを読み込んで該当する連絡の isFavorite を更新
      matchingKeys.forEach((key: string) => {
        // ページネーションキャッシュの場合
        if (key.endsWith("_pagination")) {
          const cached = storageHelpers.getObject<ContactListPaginationCache>(key)
          if (!cached) return

          let updated = false
          const updatedPages = { ...cached.pages }

          // 各ページ内の連絡を検索して更新
          Object.keys(updatedPages).forEach((pageIndexStr) => {
            const pageIndex = parseInt(pageIndexStr, 10)
            const page = updatedPages[pageIndex]
            const updatedContacts = page.contacts.map((contact) => {
              if (contact.id === contactId) {
                updated = true
                return { ...contact, isFavorite }
              }
              return contact
            })

            if (updated) {
              updatedPages[pageIndex] = {
                ...page,
                contacts: updatedContacts,
              }
            }
          })

          if (updated) {
            const updatedCache: ContactListPaginationCache = {
              ...cached,
              pages: updatedPages,
              lastUpdatedAt: Date.now(),
            }
            storageHelpers.setObject(key, updatedCache)
            console.log("[Pagination Cache] Updated isFavorite:", { key, contactId, isFavorite })
          }
        } else {
          // 従来のキャッシュの場合
          const cached = storageHelpers.getObject<ContactListCache>(key)
          if (!cached) return

          const updatedContacts = cached.contacts.map((contact) =>
            contact.id === contactId ? { ...contact, isFavorite } : contact
          )

          const updatedCache: ContactListCache = {
            ...cached,
            contacts: updatedContacts,
          }

          storageHelpers.setObject(key, updatedCache)
          console.log("[Cache] Updated isFavorite in cache:", { key, contactId, isFavorite })
        }
      })
    } catch (error) {
      console.error("[Cache] Error updating contact favorite", error)
    }
  }

  /**
   * キャッシュから特定の連絡が未読かどうかを取得
   * @returns true: 未読, false: 既読, undefined: キャッシュに存在しない
   */
  static isContactUnreadInCache(params: { teamMemberId: string; contactId: string }): boolean | undefined {
    try {
      const { teamMemberId, contactId } = params

      // inbox コンテナの全キャッシュキーを取得
      const allKeys = storageHelpers.getAllKeys()
      const matchingKeys = allKeys.filter((key: string) =>
        key.includes(`${CACHE_KEY_PREFIX.CONTACT_LIST}_${teamMemberId}_inbox_`)
      )

      for (const key of matchingKeys) {
        // ページネーションキャッシュの場合
        if (key.endsWith("_pagination")) {
          const cached = storageHelpers.getObject<ContactListPaginationCache>(key)
          if (!cached) continue

          // 各ページ内の連絡を検索
          for (const pageIndexStr of Object.keys(cached.pages)) {
            const pageIndex = parseInt(pageIndexStr, 10)
            const page = cached.pages[pageIndex]
            const contact = page.contacts.find((c) => c.id === contactId)
            if (contact) {
              return !contact.isRead
            }
          }
        } else {
          // 従来のキャッシュの場合
          const cached = storageHelpers.getObject<ContactListCache>(key)
          if (!cached) continue

          const contact = cached.contacts.find((c) => c.id === contactId)
          if (contact) {
            return !contact.isRead
          }
        }
      }

      // キャッシュに存在しない
      return undefined
    } catch (error) {
      console.error("[Cache] Error checking contact read status", error)
      return undefined
    }
  }

  /**
   * 特定の連絡を既読にする（キャッシュ内のデータを更新）
   */
  static markContactAsRead(params: { teamMemberId: string; contactId: string }): void {
    try {
      const { teamMemberId, contactId } = params
      console.log("[Cache] markContactAsRead:", { teamMemberId, contactId })

      // inbox コンテナの全キャッシュキーを取得
      const pattern = `${CACHE_KEY_PREFIX.CONTACT_LIST}_${teamMemberId}_inbox_*`
      const allKeys = storageHelpers.getAllKeys()
      const matchingKeys = allKeys.filter((key: string) =>
        key.includes(`${CACHE_KEY_PREFIX.CONTACT_LIST}_${teamMemberId}_inbox_`)
      )

      // 各キャッシュを読み込んで該当する連絡の isRead を更新
      matchingKeys.forEach((key: string) => {
        // ページネーションキャッシュの場合
        if (key.endsWith("_pagination")) {
          const cached = storageHelpers.getObject<ContactListPaginationCache>(key)
          if (!cached) return

          let updated = false
          const updatedPages = { ...cached.pages }

          // 各ページ内の連絡を検索して更新
          Object.keys(updatedPages).forEach((pageIndexStr) => {
            const pageIndex = parseInt(pageIndexStr, 10)
            const page = updatedPages[pageIndex]
            const updatedContacts = page.contacts.map((contact) => {
              if (contact.id === contactId) {
                updated = true
                return { ...contact, isRead: true }
              }
              return contact
            })

            if (updated) {
              updatedPages[pageIndex] = {
                ...page,
                contacts: updatedContacts,
              }
            }
          })

          if (updated) {
            const updatedCache: ContactListPaginationCache = {
              ...cached,
              pages: updatedPages,
              lastUpdatedAt: Date.now(),
            }
            storageHelpers.setObject(key, updatedCache)
            console.log("[Pagination Cache] Updated isRead:", { key, contactId })
          }
        } else {
          // 従来のキャッシュの場合
          const cached = storageHelpers.getObject<ContactListCache>(key)
          if (!cached) return

          const updatedContacts = cached.contacts.map((contact) =>
            contact.id === contactId ? { ...contact, isRead: true } : contact
          )

          const updatedCache: ContactListCache = {
            ...cached,
            contacts: updatedContacts,
          }

          storageHelpers.setObject(key, updatedCache)
          console.log("[Cache] Updated isRead in cache:", { key, contactId })
        }
      })
    } catch (error) {
      console.error("[Cache] Error marking contact as read", error)
    }
  }

  /**
   * イベント駆動のキャッシュ無効化
   */
  static invalidateByEvent(
    event: CacheInvalidationEvent,
    context: {
      teamMemberId: string
      contactId?: string
      containerType?: ContactContainerType
    }
  ): void {
    try {
      switch (event) {
        case CacheInvalidationEvent.PULL_TO_REFRESH:
          // 現在のコンテナのみ無効化（明示的なユーザー操作）
          if (context.containerType) {
            this.invalidateContainer(context.teamMemberId, context.containerType)
          }
          break

        case CacheInvalidationEvent.PUSH_NOTIFICATION_OPENED:
          // 受信トレイのキャッシュをクリア（新着連絡があるため）
          this.invalidateContainer(context.teamMemberId, "inbox")
          break

        case CacheInvalidationEvent.CONTACT_SENT:
          // 送信トレイのみ無効化
          this.invalidateContainer(context.teamMemberId, "outbox")
          break

        case CacheInvalidationEvent.CONTACT_UPDATED:
        case CacheInvalidationEvent.CONTACT_DELETED:
          // 連絡一覧の全無効化
          this.invalidateAll(context.teamMemberId)
          // 連絡詳細も無効化
          if (context.contactId) {
            this.deleteDetailByContactId(context.contactId)
          }
          break

        case CacheInvalidationEvent.FAVORITE_TOGGLED:
          // お気に入り状態はupdateContactFavoriteでインプレース更新されるため
          // キャッシュを無効化せず、インプレース更新に任せる
          // これにより、既存のキャッシュを保持しつつUIの体感速度を向上
          // 注: お気に入りフィルタが適用されている場合、フィルタ結果が変わる可能性があるが
          // 次回のPull-to-Refreshまたはアプリ再起動時に正しいデータが取得される
          console.log("[Cache] FAVORITE_TOGGLED - Using in-place update, no invalidation needed")
          break

        case CacheInvalidationEvent.READ_STATUS_CHANGED:
          // 既読状態はローカルで即座に反映（キャッシュ無効化不要）
          // サーバー同期は次回更新時
          break

        default:
          console.warn("[Cache] Unknown invalidation event", event)
      }

      console.log("[Cache] INVALIDATE_BY_EVENT", { event, context })
    } catch (error) {
      console.error("[Cache] Error invalidating by event", error)
    }
  }

  /**
   * 全ユーザーの受信トレイキャッシュをクリア（アプリ起動時用）
   */
  static clearAllInboxCache(): void {
    try {
      // 全ての受信トレイキャッシュを削除
      // Note: storageHelpers.deleteKeysWithPrefixはpattern.replace("*", "")で
      // 最初の*のみを置換するため、"_inbox_"を含むキーを個別にフィルタリング
      const allKeys = storage.getAllKeys()
      const inboxKeys = allKeys.filter(
        (key: string) => key.startsWith(CACHE_KEY_PREFIX.CONTACT_LIST) && key.includes("_inbox_")
      )

      inboxKeys.forEach((key: string) => {
        storage.remove(key)
      })

      console.log("[Cache] CLEAR_ALL_INBOX - All inbox caches cleared on app launch", {
        deletedCount: inboxKeys.length,
      })
    } catch (error) {
      console.error("[Cache] Error clearing all inbox caches", error)
    }
  }

  /**
   * 全キャッシュをクリア（デバッグ用）
   */
  static clearAll(): void {
    try {
      // 連絡一覧キャッシュ
      const listPattern = `${CACHE_KEY_PREFIX.CONTACT_LIST}_*`
      storageHelpers.deleteKeysWithPrefix(listPattern)

      // 連絡詳細キャッシュ
      const detailPattern = `${CACHE_KEY_PREFIX.CONTACT_DETAIL}_*`
      storageHelpers.deleteKeysWithPrefix(detailPattern)

      console.log("[Cache] CLEAR_ALL - All caches cleared (list + detail)")
    } catch (error) {
      console.error("[Cache] Error clearing all caches", error)
    }
  }

  /**
   * キャッシュ統計情報を取得（デバッグ用）
   */
  static getStats(): {
    totalKeys: number
    totalSize: number
    sizeInMB: number
  } {
    try {
      const allKeys = storage.getAllKeys()
      const contactListKeys = allKeys.filter((key: string) => key.startsWith(CACHE_KEY_PREFIX.CONTACT_LIST))

      const totalSize = storageHelpers.getTotalSize()
      const sizeInMB = totalSize / (1024 * 1024)

      return {
        totalKeys: contactListKeys.length,
        totalSize,
        sizeInMB: Math.round(sizeInMB * 100) / 100,
      }
    } catch (error) {
      console.error("[Cache] Error getting stats", error)
      return {
        totalKeys: 0,
        totalSize: 0,
        sizeInMB: 0,
      }
    }
  }

  /**
   * ヘルスチェック - 全キャッシュの整合性を確認し、必要に応じて修復
   */
  static async healthCheck(): Promise<HealthCheckResult> {
    const result: HealthCheckResult = {
      healthy: true,
      issues: [],
      repaired: [],
      deleted: [],
      totalChecked: 0,
      corruptedCount: 0,
    }

    try {
      const allKeys = storage.getAllKeys()
      const cacheKeys = allKeys.filter(
        (key: string) => key.startsWith(CACHE_KEY_PREFIX.CONTACT_LIST) || key.startsWith(CACHE_KEY_PREFIX.CONTACT_DETAIL)
      )

      result.totalChecked = cacheKeys.length
      console.log(`[Cache] Health check starting - ${cacheKeys.length} keys to check`)

      for (const key of cacheKeys) {
        try {
          // バックアップキーはスキップ
          if (key.includes("_backup")) continue

          const data = storageHelpers.getObject(key)

          if (!data) {
            result.issues.push(`Empty data: ${key}`)
            result.deleted.push(key)
            storageHelpers.delete(key)
            continue
          }

          // チェックサム検証
          const dataWithChecksum = data as any
          if (dataWithChecksum.__checksum && !ContactListCacheService.verifyChecksum(dataWithChecksum)) {
            result.corruptedCount++
            console.warn(`[Cache] Corrupted data found: ${key}`)

            // 修復を試行
            const repaired = ContactListCacheService.attemptRepair(key)
            if (repaired) {
              result.repaired.push(key)
              console.log(`[Cache] Successfully repaired: ${key}`)
            } else {
              result.deleted.push(key)
              storageHelpers.delete(key)
              console.log(`[Cache] Deleted corrupted cache: ${key}`)
            }
            result.healthy = false
          }

          // 期限切れデータの削除
          if (dataWithChecksum.expiresAt && dataWithChecksum.expiresAt < Date.now()) {
            result.deleted.push(key)
            storageHelpers.delete(key)
            this.monitor.recordEviction()
          }
        } catch (error) {
          result.issues.push(`Error checking ${key}: ${error}`)
          result.healthy = false
        }
      }

      // 統計情報を更新
      const stats = this.getStats()
      console.log("[Cache] Health check completed", {
        ...result,
        currentSize: `${stats.sizeInMB}MB`,
        totalKeys: stats.totalKeys,
      })

      // モニターにヘルスチェック結果を記録
      if (result.corruptedCount > 0) {
        this.monitor.recordError(new Error(`Found ${result.corruptedCount} corrupted caches`), "healthCheck")
      }
    } catch (error) {
      console.error("[Cache] Health check failed", error)
      result.healthy = false
      result.error = error as Error
    }

    return result
  }

  /**
   * キャッシュクリーンアップ - 期限切れ・破損データを削除
   */
  static async cleanup(): Promise<CleanupResult> {
    const result: CleanupResult = {
      deletedKeys: [],
      freedSpace: 0,
      errors: [],
    }

    try {
      const beforeSize = this.getStats().totalSize
      const healthCheck = await this.healthCheck()

      result.deletedKeys = healthCheck.deleted
      const afterSize = this.getStats().totalSize
      result.freedSpace = beforeSize - afterSize

      console.log("[Cache] Cleanup completed", {
        deleted: result.deletedKeys.length,
        freedSpaceMB: (result.freedSpace / (1024 * 1024)).toFixed(2),
      })
    } catch (error) {
      console.error("[Cache] Cleanup failed", error)
      result.errors.push({ key: "cleanup", error: error as Error })
    }

    return result
  }

  // ========================================
  // 連絡詳細キャッシュメソッド
  // ========================================

  /**
   * 連絡詳細のキャッシュキーを生成
   */
  private static generateDetailCacheKey(contactId: string, contactType: string): string {
    return `${CACHE_KEY_PREFIX.CONTACT_DETAIL}_${contactId}_${contactType}`
  }

  /**
   * 連絡詳細のTTLを取得
   */
  private static getDetailTTL(contactType: "Inbox" | "Outbox" | "Reserved" | "Migration"): number {
    // 連絡詳細は内容が変更されにくいため、長めのTTLを設定
    switch (contactType) {
      case "Inbox":
        return 48 * 60 * 60 * 1000 // 48時間（受信後は基本的に変更なし）
      case "Outbox":
        return 7 * 24 * 60 * 60 * 1000 // 7日（自分が送信したもの、変更されない）
      case "Reserved":
        return 24 * 60 * 60 * 1000 // 24時間（予約送信、送信前に変更の可能性）
      case "Migration":
        return 30 * 24 * 60 * 60 * 1000 // 30日（移行データ、変更されない）
    }
  }

  /**
   * 連絡詳細のキャッシュを取得
   */
  static getDetail(params: {
    contactId: string
    contactType: "Inbox" | "Outbox" | "Reserved" | "Migration"
  }): ContactDetailCache | null {
    try {
      const cacheKey = this.generateDetailCacheKey(params.contactId, params.contactType)
      const cached = storageHelpers.getObject<ContactDetailCache>(cacheKey)

      if (!cached) {
        console.log("[Cache Detail] MISS - No cache found", { contactId: params.contactId })
        return null
      }

      // バージョンチェック
      if (cached.version !== this.CURRENT_VERSION) {
        console.warn("[Cache Detail] Version mismatch - Invalidating cache")
        this.deleteDetail(params)
        return null
      }

      const now = Date.now()

      // TTL確認
      if (cached.expiresAt <= now) {
        console.log("[Cache Detail] EXPIRED - TTL exceeded", { contactId: params.contactId })
        this.deleteDetail(params)
        return null
      }

      // Date フィールドの復元
      const restoredData = this.restoreDateFields(cached.data)

      console.log("[Cache Detail] HIT - Valid cache found", {
        contactId: params.contactId,
        cachedAt: new Date(cached.cachedAt).toISOString(),
        expiresAt: new Date(cached.expiresAt).toISOString(),
      })

      return {
        ...cached,
        data: restoredData,
      }
    } catch (error) {
      console.error("[Cache Detail] Error getting cache", error)
      return null
    }
  }

  /**
   * キャッシュから取得したデータの Date フィールドを復元（最適化版）
   * 配列の場合は一括処理、オブジェクトの場合は単体処理
   * ネストされた構造（schedules, surveys, eventDateCandidate等）も処理
   */
  private static restoreDateFields(data: any): any {
    if (!data) return data

    // Date フィールドリスト（静的に定義して毎回配列を作らない）
    const DATE_FIELDS = [
      "sentDate",
      "reservedDate",
      "updatedDate",
      "deadlineDate",
      "reminderDate",
      "startDate",
      "endDate",
      "timeLimitDate",
    ]

    // 配列の場合はバッチ処理
    if (Array.isArray(data)) {
      return data.map((item) => {
        if (!item || typeof item !== "object") return item
        const restored = { ...item }
        // ループを最小化
        for (let i = 0; i < DATE_FIELDS.length; i++) {
          const field = DATE_FIELDS[i]
          if (restored[field]) {
            restored[field] = new Date(restored[field])
          }
        }
        return restored
      })
    }

    // 単一オブジェクトの場合
    const restored = { ...data }

    // 最上位のDateフィールドを復元
    for (let i = 0; i < DATE_FIELDS.length; i++) {
      const field = DATE_FIELDS[i]
      if (restored[field]) {
        restored[field] = new Date(restored[field])
      }
    }

    // ネストされた構造の処理: schedules配列（日程調整）
    if (restored.schedules && Array.isArray(restored.schedules)) {
      restored.schedules = restored.schedules.map((schedule: any) => {
        if (!schedule || typeof schedule !== "object") return schedule
        const restoredSchedule = { ...schedule }

        // eventDateCandidate内のDateフィールドを復元
        if (restoredSchedule.eventDateCandidate && typeof restoredSchedule.eventDateCandidate === "object") {
          const candidate = { ...restoredSchedule.eventDateCandidate }
          if (candidate.startDate) {
            candidate.startDate = new Date(candidate.startDate)
          }
          if (candidate.endDate) {
            candidate.endDate = new Date(candidate.endDate)
          }
          restoredSchedule.eventDateCandidate = candidate
        }

        return restoredSchedule
      })
    }

    // ネストされた構造の処理: surveys配列（アンケート）
    if (restored.surveys && Array.isArray(restored.surveys)) {
      restored.surveys = restored.surveys.map((survey: any) => {
        if (!survey || typeof survey !== "object") return survey
        const restoredSurvey = { ...survey }

        // survey内のDateフィールドを復元
        for (let i = 0; i < DATE_FIELDS.length; i++) {
          const field = DATE_FIELDS[i]
          if (restoredSurvey[field]) {
            restoredSurvey[field] = new Date(restoredSurvey[field])
          }
        }

        return restoredSurvey
      })
    }

    return restored
  }

  /**
   * 連絡詳細のキャッシュを保存
   */
  static setDetail(
    params: {
      contactId: string
      contactType: "Inbox" | "Outbox" | "Reserved" | "Migration"
    },
    data: any
  ): void {
    try {
      const cacheKey = this.generateDetailCacheKey(params.contactId, params.contactType)
      const ttl = this.getDetailTTL(params.contactType)
      const now = Date.now()

      const cache: ContactDetailCache = {
        version: this.CURRENT_VERSION,
        contactId: params.contactId,
        contactType: params.contactType,
        data,
        cachedAt: now,
        expiresAt: now + ttl,
      }

      storageHelpers.setObject(cacheKey, cache)

      console.log("[Cache Detail] SET - Cache saved", {
        contactId: params.contactId,
        ttl: `${ttl / 1000 / 60 / 60}h`,
      })
    } catch (error) {
      console.error("[Cache Detail] Error setting cache", error)
    }
  }

  /**
   * 連絡詳細のキャッシュを削除
   */
  static deleteDetail(params: { contactId: string; contactType: "Inbox" | "Outbox" | "Reserved" | "Migration" }): void {
    try {
      const cacheKey = this.generateDetailCacheKey(params.contactId, params.contactType)
      storageHelpers.delete(cacheKey)
      console.log("[Cache Detail] DELETE - Cache deleted", { contactId: params.contactId })
    } catch (error) {
      console.error("[Cache Detail] Error deleting cache", error)
    }
  }

  /**
   * 特定の連絡IDに関連するすべてのキャッシュを削除
   * （Inbox/Outbox/Reserved/Migrationすべて）
   */
  static deleteDetailByContactId(contactId: string): void {
    try {
      const types: Array<"Inbox" | "Outbox" | "Reserved" | "Migration"> = ["Inbox", "Outbox", "Reserved", "Migration"]
      types.forEach((type) => {
        const cacheKey = this.generateDetailCacheKey(contactId, type)
        storageHelpers.delete(cacheKey)
      })
      console.log("[Cache Detail] DELETE_BY_ID - All detail caches deleted", { contactId })
    } catch (error) {
      console.error("[Cache Detail] Error deleting detail by contactId", error)
    }
  }

  // ============================================================
  // 返信スレッドキャッシュ
  // ============================================================

  private static readonly REPLY_THREADS_TTL = 5 * 60 * 1000 // 5分

  /**
   * 返信スレッドのキャッシュキーを生成
   */
  private static generateReplyThreadsCacheKey(contactId: string, viewMode: string): string {
    return `${CACHE_KEY_PREFIX.REPLY_THREADS}_${contactId}_${viewMode}`
  }

  /**
   * 返信スレッドのキャッシュを取得
   */
  static getReplyThreads(params: { contactId: string; viewMode: "Inbox" | "Outbox" }): ReplyThreadsCache | null {
    try {
      const cacheKey = this.generateReplyThreadsCacheKey(params.contactId, params.viewMode)
      const cached = storageHelpers.getObject<ReplyThreadsCache>(cacheKey)

      if (!cached) {
        console.log("[Cache ReplyThreads] MISS - No cache found", { contactId: params.contactId })
        return null
      }

      // バージョンチェック
      if (cached.version !== this.CURRENT_VERSION) {
        console.warn("[Cache ReplyThreads] Version mismatch - Invalidating cache")
        this.deleteReplyThreads(params)
        return null
      }

      const now = Date.now()

      // TTL確認
      if (cached.expiresAt <= now) {
        console.log("[Cache ReplyThreads] EXPIRED - TTL exceeded", { contactId: params.contactId })
        this.deleteReplyThreads(params)
        return null
      }

      // Dateフィールドの復元
      const restoredData = cached.data.map((item: any) => ({
        ...item,
        recentMessage: item.recentMessage
          ? {
              ...item.recentMessage,
              updatedAt: item.recentMessage.updatedAt ? new Date(item.recentMessage.updatedAt) : undefined,
            }
          : item.recentMessage,
      }))

      console.log("[Cache ReplyThreads] HIT - Valid cache found", {
        contactId: params.contactId,
        itemCount: cached.data.length,
      })

      return {
        ...cached,
        data: restoredData,
      }
    } catch (error) {
      console.error("[Cache ReplyThreads] Error getting cache", error)
      return null
    }
  }

  /**
   * 返信スレッドのキャッシュを保存
   */
  static setReplyThreads(
    params: {
      contactId: string
      viewMode: "Inbox" | "Outbox"
    },
    data: any[]
  ): void {
    try {
      const cacheKey = this.generateReplyThreadsCacheKey(params.contactId, params.viewMode)
      const now = Date.now()

      const cache: ReplyThreadsCache = {
        version: this.CURRENT_VERSION,
        contactId: params.contactId,
        viewMode: params.viewMode,
        data,
        cachedAt: now,
        expiresAt: now + this.REPLY_THREADS_TTL,
      }

      storageHelpers.setObject(cacheKey, cache)

      console.log("[Cache ReplyThreads] SET - Cache saved", {
        contactId: params.contactId,
        itemCount: data.length,
        ttl: `${this.REPLY_THREADS_TTL / 1000 / 60}m`,
      })
    } catch (error) {
      console.error("[Cache ReplyThreads] Error setting cache", error)
    }
  }

  /**
   * 返信スレッドのキャッシュを削除
   */
  static deleteReplyThreads(params: { contactId: string; viewMode: "Inbox" | "Outbox" }): void {
    try {
      const cacheKey = this.generateReplyThreadsCacheKey(params.contactId, params.viewMode)
      storageHelpers.delete(cacheKey)
      console.log("[Cache ReplyThreads] DELETE - Cache deleted", { contactId: params.contactId })
    } catch (error) {
      console.error("[Cache ReplyThreads] Error deleting cache", error)
    }
  }

  /**
   * 特定の連絡IDに関連する返信スレッドキャッシュを全て削除
   */
  static deleteReplyThreadsByContactId(contactId: string): void {
    try {
      const viewModes: Array<"Inbox" | "Outbox"> = ["Inbox", "Outbox"]
      viewModes.forEach((viewMode) => {
        const cacheKey = this.generateReplyThreadsCacheKey(contactId, viewMode)
        storageHelpers.delete(cacheKey)
      })
      console.log("[Cache ReplyThreads] DELETE_BY_ID - All reply threads caches deleted", { contactId })
    } catch (error) {
      console.error("[Cache ReplyThreads] Error deleting by contactId", error)
    }
  }

  // ============================================================
  // チームメンバー回答状況キャッシュ
  // ============================================================

  private static readonly TEAM_MEMBER_CONTACTS_TTL = 5 * 60 * 1000 // 5分

  /**
   * チームメンバー回答状況のキャッシュキーを生成
   */
  private static generateTeamMemberContactsCacheKey(contactId: string, type: string): string {
    return `${CACHE_KEY_PREFIX.TEAM_MEMBER_CONTACTS}_${contactId}_${type}`
  }

  /**
   * チームメンバー回答状況のキャッシュを取得
   */
  static getTeamMemberContacts(params: {
    contactId: string
    type: "Inbox" | "Outbox" | "Reserved"
  }): TeamMemberContactsCache | null {
    try {
      const cacheKey = this.generateTeamMemberContactsCacheKey(params.contactId, params.type)
      const cached = storageHelpers.getObject<TeamMemberContactsCache>(cacheKey)

      if (!cached) {
        console.log("[Cache TeamMemberContacts] MISS - No cache found", { contactId: params.contactId })
        return null
      }

      // バージョンチェック
      if (cached.version !== this.CURRENT_VERSION) {
        console.warn("[Cache TeamMemberContacts] Version mismatch - Invalidating cache")
        this.deleteTeamMemberContacts(params)
        return null
      }

      const now = Date.now()

      // TTL確認
      if (cached.expiresAt <= now) {
        console.log("[Cache TeamMemberContacts] EXPIRED - TTL exceeded", { contactId: params.contactId })
        this.deleteTeamMemberContacts(params)
        return null
      }

      console.log("[Cache TeamMemberContacts] HIT - Valid cache found", {
        contactId: params.contactId,
        itemCount: cached.data?.items?.length ?? 0,
      })

      return cached
    } catch (error) {
      console.error("[Cache TeamMemberContacts] Error getting cache", error)
      return null
    }
  }

  /**
   * チームメンバー回答状況のキャッシュを保存
   */
  static setTeamMemberContacts(
    params: {
      contactId: string
      type: "Inbox" | "Outbox" | "Reserved"
    },
    data: any
  ): void {
    try {
      const cacheKey = this.generateTeamMemberContactsCacheKey(params.contactId, params.type)
      const now = Date.now()

      const cache: TeamMemberContactsCache = {
        version: this.CURRENT_VERSION,
        contactId: params.contactId,
        type: params.type,
        data,
        cachedAt: now,
        expiresAt: now + this.TEAM_MEMBER_CONTACTS_TTL,
      }

      storageHelpers.setObject(cacheKey, cache)

      console.log("[Cache TeamMemberContacts] SET - Cache saved", {
        contactId: params.contactId,
        itemCount: data?.items?.length ?? 0,
        ttl: `${this.TEAM_MEMBER_CONTACTS_TTL / 1000 / 60}m`,
      })
    } catch (error) {
      console.error("[Cache TeamMemberContacts] Error setting cache", error)
    }
  }

  /**
   * チームメンバー回答状況のキャッシュを削除
   */
  static deleteTeamMemberContacts(params: { contactId: string; type: "Inbox" | "Outbox" | "Reserved" }): void {
    try {
      const cacheKey = this.generateTeamMemberContactsCacheKey(params.contactId, params.type)
      storageHelpers.delete(cacheKey)
      console.log("[Cache TeamMemberContacts] DELETE - Cache deleted", { contactId: params.contactId })
    } catch (error) {
      console.error("[Cache TeamMemberContacts] Error deleting cache", error)
    }
  }

  /**
   * 特定の連絡IDに関連するチームメンバー回答状況キャッシュを全て削除
   */
  static deleteTeamMemberContactsByContactId(contactId: string): void {
    try {
      const types: Array<"Inbox" | "Outbox" | "Reserved"> = ["Inbox", "Outbox", "Reserved"]
      types.forEach((type) => {
        const cacheKey = this.generateTeamMemberContactsCacheKey(contactId, type)
        storageHelpers.delete(cacheKey)
      })
      console.log("[Cache TeamMemberContacts] DELETE_BY_ID - All team member contacts caches deleted", { contactId })
    } catch (error) {
      console.error("[Cache TeamMemberContacts] Error deleting by contactId", error)
    }
  }

  /**
   * 連絡詳細関連のすべてのキャッシュを削除（詳細、返信スレッド、メンバー回答状況）
   */
  static deleteAllDetailCachesByContactId(contactId: string): void {
    this.deleteDetailByContactId(contactId)
    this.deleteReplyThreadsByContactId(contactId)
    this.deleteTeamMemberContactsByContactId(contactId)
    console.log("[Cache] DELETE_ALL_DETAIL - All detail-related caches deleted", { contactId })
  }
}

/**
 * ヘルスチェック結果
 */
export interface HealthCheckResult {
  healthy: boolean
  issues: string[]
  repaired: string[]
  deleted: string[]
  totalChecked: number
  corruptedCount: number
  error?: Error
}

/**
 * クリーンアップ結果
 */
export interface CleanupResult {
  deletedKeys: string[]
  freedSpace: number
  errors: Array<{ key: string; error: Error }>
}
